import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Checkbox,
  Typography,
  Space,
  Tag,
  Spin,
  Alert,
  Divider,
  message,
  Radio,
} from 'antd';
import {
  PlusOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  CloudServerOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const DatasetCreate: React.FC = () => {
  const [form] = Form.useForm();
  const [selectedBuckets, setSelectedBuckets] = useState<string[]>([]);
  const [availableBuckets, setAvailableBuckets] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBuckets();
  }, []);

  const fetchBuckets = async () => {
    try {
      const response = await axios.get(`${API_URL}/buckets`);
      setAvailableBuckets(response.data);
    } catch (error) {
      console.error('Failed to fetch buckets:', error);
      message.error('Failed to load buckets');
    } finally {
      setLoading(false);
    }
  };

  const toggleBucket = (bucket: string) => {
    if (selectedBuckets.includes(bucket)) {
      setSelectedBuckets(selectedBuckets.filter(b => b !== bucket));
    } else {
      setSelectedBuckets([...selectedBuckets, bucket]);
    }
  };

  const handleSubmit = async (values: { name: string; type: string }) => {
    if (selectedBuckets.length === 0) {
      message.error('Please select at least one bucket');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/datasets`, {
        name: values.name.trim(),
        buckets: selectedBuckets,
        type: values.type || 'Photo'
      });
      message.success('Dataset created successfully');
      navigate('/datasets');
    } catch (error: any) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Failed to create dataset');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/datasets')}
          style={{ marginBottom: '16px' }}
        >
          Back to Datasets
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          <PlusOutlined style={{ marginRight: '12px', color: '#6366f1' }} />
          Create Dataset
        </Title>
      </div>

      <Card style={{ maxWidth: '700px' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size="large"
        >
          <Form.Item
            name="name"
            label="Dataset Name"
            rules={[{ required: true, message: 'Please enter a dataset name' }]}
          >
            <Input placeholder="Enter dataset name" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Dataset Type"
            initialValue="Photo"
          >
            <Radio.Group>
              <Radio.Button value="Photo">Photo</Radio.Button>
              <Radio.Button value="Anime">Anime</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="Select Buckets">
            {selectedBuckets.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                  Selected buckets:
                </Text>
                <Space size={[8, 8]} wrap>
                  {selectedBuckets.map(bucket => (
                    <Tag
                      key={bucket}
                      color="#6366f1"
                      closable
                      onClose={() => toggleBucket(bucket)}
                      style={{ padding: '4px 12px', fontSize: '14px' }}
                    >
                      <CloudServerOutlined style={{ marginRight: '6px' }} />
                      {bucket}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}

            <Card
              size="small"
              style={{ maxHeight: '300px', overflow: 'auto' }}
              bodyStyle={{ padding: 0 }}
            >
              {availableBuckets.map((bucket, index) => (
                <div
                  key={bucket}
                  onClick={() => toggleBucket(bucket)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    borderBottom: index < availableBuckets.length - 1 ? '1px solid #f0f0f0' : 'none',
                    backgroundColor: selectedBuckets.includes(bucket) ? '#f0f5ff' : 'white',
                    transition: 'background-color 0.2s',
                  }}
                >
                  <Checkbox checked={selectedBuckets.includes(bucket)} />
                  <CloudServerOutlined style={{ color: '#6366f1' }} />
                  <Text>{bucket}</Text>
                </div>
              ))}
            </Card>

            {selectedBuckets.length === 0 && (
              <Alert
                message="Select at least one bucket to create a dataset"
                type="info"
                showIcon
                style={{ marginTop: '12px' }}
              />
            )}
          </Form.Item>

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button onClick={() => navigate('/datasets')}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                icon={<PlusOutlined />}
              >
                Create Dataset
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default DatasetCreate;
